/**
 * Shared @mention engine.
 *
 * Pure, DOM-free and framework-free so that web (contenteditable span) and
 * NativeScript (TextView) can run the exact same detection rules. Previously
 * each platform had its own algorithm: web accumulated the query keystroke by
 * keystroke through an a-z allowlist, NativeScript re-scanned the whole text
 * with a regex and took the last match. They disagreed constantly.
 *
 * The rule here is simply: derive the query from the text and the caret, every
 * time. Nothing is remembered between keystrokes, so backspacing back into a
 * mention re-opens it for free and there is no bookkeeping to get out of sync.
 */

/** Max words a query may span. Covers "Studio One Experts", "Jean Luc Picard". */
export const MAX_MENTION_WORDS = 3;

/** Max characters between the "@" and the caret. */
export const MAX_MENTION_QUERY_LENGTH = 40;

/** Results shown in the picker, on every surface. */
export const MENTION_RESULT_LIMIT = 10;

/**
 * Finds the last "@" that sits at a word boundary and captures everything
 * between it and the caret.
 *
 * The regex deliberately answers only "where does the candidate start, and what
 * is it". It does NOT try to encode when to stop, because the most important
 * stop condition depends on how many results the query matched -- something no
 * regex can know. That lives in `shouldKeepMentionOpen`.
 *
 * Newlines are excluded from the capture, so a mention can never span lines.
 * The {0,40} bound enforces MAX_MENTION_QUERY_LENGTH: once the user types past
 * it there is simply no match, and the picker closes.
 */
const MENTION_TRIGGER = /(?:^|[\s(\[{«"'])@([^@\n\r]{0,40})$/;

/** Two spaces in a row means the user moved on to writing a sentence. */
const DOUBLE_SPACE = /[  ]{2}/;

/** Contenteditable emits &nbsp; freely, so treat it as whitespace everywhere. */
const WHITESPACE = /[\s ]+/;

export interface MentionContext {
  /** Verbatim text between the "@" and the caret. May contain spaces. */
  query: string;
  /** Index of the "@" character in the source text. */
  triggerIndex: number;
  /** Index just past the query, i.e. where the caret was. */
  caretIndex: number;
  /** Words in the trimmed query. A trailing space does not add a word. */
  wordCount: number;
}

export interface MentionCandidate {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  photo?: string;
  /** Picker highlight state. Mutated by the picker, not by this module. */
  selected?: boolean;
}

export interface MentionRecord {
  id: string;
  /** The literal text in the message body, e.g. "@Dave Coffin". */
  selector: string;
  /** Character offset of the selector in the plain-text message. */
  start?: number;
  /** Character offset just past the selector. */
  end?: number;
  user?: any;
  /** Legacy: the editor HTML the mention was inserted as. */
  editorSelector?: string;
}

/**
 * Derives the active mention context from text + caret position.
 *
 * Returns null when the user is not composing a mention. Callers should treat
 * null as "close the picker".
 *
 * @param text        Plain text of the input.
 * @param caretOffset Character offset of the caret. Defaults to end of text,
 *                    which is what NativeScript uses when the platform will not
 *                    hand us a reliable selection index.
 */
export function detectMentionContext(text: string, caretOffset?: number): MentionContext | null {
  if (!text) return null;

  const caret = typeof caretOffset === "number" ? Math.max(0, Math.min(caretOffset, text.length)) : text.length;

  const match = MENTION_TRIGGER.exec(text.slice(0, caret));
  if (!match) return null;

  const query = match[1];

  // A second space run means they finished the name and kept typing.
  if (DOUBLE_SPACE.test(query)) return null;

  const wordCount = countMentionWords(query);
  if (wordCount > MAX_MENTION_WORDS) return null;

  // match.index points at the boundary char (or at the "@" itself when the
  // mention starts the text), so the "@" is the last char of match[0] minus the
  // query length.
  const triggerIndex = match.index + match[0].length - query.length - 1;

  return { query, triggerIndex, caretIndex: caret, wordCount };
}

/** Words in the trimmed query. "Dave " is one word, not two. */
export function countMentionWords(query: string): number {
  const trimmed = (query || "").trim();
  return trimmed ? trimmed.split(WHITESPACE).length : 0;
}

/**
 * The result-aware half of the stop logic, and the reason typing spaces is safe.
 *
 * A single word stays open even with no matches -- the user may still be typing,
 * and a prefix search legitimately returns nothing for the first character or
 * two. But once the query spans a space and still matches nobody, they are
 * writing prose, not a name, so we get out of the way immediately.
 *
 *   "@Dave "       -> one word (trailing space ignored), stays open
 *   "@Dave C"      -> two words, matches "Dave Coffin", stays open
 *   "@Dave is"     -> two words, matches nothing, closes on that keystroke
 */
export function shouldKeepMentionOpen(context: MentionContext | null, resultCount: number): boolean {
  if (!context) return false;
  if (context.wordCount >= 2 && resultCount === 0) return false;
  return true;
}

/** Display name for a candidate, tolerating both profile shapes. */
export function mentionDisplayName(candidate: MentionCandidate): string {
  if (!candidate) return "";
  if (candidate.name) return candidate.name;
  return [candidate.firstName, candidate.lastName].filter(Boolean).join(" ");
}

/** The literal text a mention occupies in the message body. */
export function mentionSelector(candidate: MentionCandidate): string {
  return "@" + mentionDisplayName(candidate);
}

/**
 * True when the query is simply a mention that has already been inserted.
 *
 * Inserting "@Gregor Beyerle " leaves text that detection happily reads back as
 * the query "Gregor Beyerle " -- which still matches Gregor -- so the picker
 * re-offered the person who had just been chosen. It showed up on mobile as the
 * list reappearing right after a pick (the ngModel write-back echoes as a text
 * change) and on web when clicking back into the field.
 *
 * The trailing whitespace is what marks an insertion as finished: a name still
 * being typed has no space after it, so "@Dave " on the way to "Dave Coffin"
 * stays open unless Dave was the person already picked.
 *
 * @param insertedSelectors The "@Name" text of mentions already in the message.
 */
export function isCompletedMention(context: MentionContext | null, insertedSelectors: string[]): boolean {
  if (!context || !insertedSelectors || !insertedSelectors.length) return false;
  if (!/[\s ]$/.test(context.query)) return false;

  const candidate = ("@" + context.query.trim()).toLowerCase();
  return insertedSelectors.some((selector) => `${selector || ""}`.toLowerCase() === candidate);
}

/**
 * Builds the persisted `mentions[]` for a plain-text composer.
 *
 * The web editor is a contenteditable and can walk its DOM for atomic chips.
 * A NativeScript TextView is plain text, so the only record of a mention is the
 * candidate list the user picked from plus the text itself. Each selector is
 * located in order, with a per-selector cursor, so mentioning the same person
 * twice yields two records at two offsets rather than both pointing at the
 * first. A candidate whose text is no longer in the message was deleted before
 * sending and is dropped -- which is what stops them being notified.
 */
export function buildMentionRecords(message: string, selected: MentionCandidate[]): MentionRecord[] {
  if (!message || !selected || !selected.length) return [];

  const cursorBySelector: { [selector: string]: number } = {};
  const records: MentionRecord[] = [];

  selected.forEach((candidate) => {
    if (!candidate || !candidate.id) return;

    const selector = mentionSelector(candidate);
    if (!selector || selector === "@") return;

    const start = message.indexOf(selector, cursorBySelector[selector] || 0);
    if (start === -1) return;

    cursorBySelector[selector] = start + selector.length;
    records.push({
      id: candidate.id,
      selector: selector,
      start: start,
      end: start + selector.length,
      user: candidate,
    });
  });

  return records;
}

export interface MentionRange {
  start: number;
  end: number;
  mention: MentionRecord;
}

/**
 * Locates each mention in the message body.
 *
 * Prefers the stored start/end offsets, which newer messages carry and which are
 * unambiguous. Falls back to scanning for the selector for legacy records --
 * but advances a per-selector cursor so that mentioning the same person (or two
 * people with the same display name) twice resolves to two different spots
 * instead of both landing on the first occurrence.
 */
export function resolveMentionRanges(message: string, mentions: MentionRecord[]): MentionRange[] {
  if (!message || !mentions || !mentions.length) return [];

  const ranges: MentionRange[] = [];
  const cursorBySelector: { [selector: string]: number } = {};

  mentions.forEach((mention) => {
    if (!mention || !mention.selector) return;
    const selector = mention.selector;

    let start = -1;
    const hasOffsets = typeof mention.start === "number" && typeof mention.end === "number";

    // Trust stored offsets only if the text actually still sits there; an edit
    // elsewhere in the message could have shifted it.
    if (hasOffsets && message.substr(mention.start, selector.length) === selector) {
      start = mention.start;
    } else {
      start = message.indexOf(selector, cursorBySelector[selector] || 0);
    }

    if (start === -1) return;

    cursorBySelector[selector] = start + selector.length;
    ranges.push({ start, end: start + selector.length, mention });
  });

  ranges.sort((a, b) => a.start - b.start);

  // Drop overlaps so segmentation can never emit interleaved garbage.
  const deduped: MentionRange[] = [];
  let consumedTo = -1;
  ranges.forEach((range) => {
    if (range.start >= consumedTo) {
      deduped.push(range);
      consumedTo = range.end;
    }
  });

  return deduped;
}

export type MessageSegment = { type: "text"; text: string } | { type: "mention"; text: string; mention: MentionRecord };

/**
 * Splits a message into alternating text and mention segments.
 *
 * Rendering walks these once and escapes/linkifies only the text segments, which
 * is what keeps a display name containing a URL-ish substring (or a "$&") from
 * being mangled by the linkifier that used to run over the whole body first.
 */
export function segmentMessageByMentions(message: string, mentions: MentionRecord[]): MessageSegment[] {
  if (!message) return [];

  const ranges = resolveMentionRanges(message, mentions);
  if (!ranges.length) return [{ type: "text", text: message }];

  const segments: MessageSegment[] = [];
  let cursor = 0;

  ranges.forEach((range) => {
    if (range.start > cursor) {
      segments.push({ type: "text", text: message.slice(cursor, range.start) });
    }
    segments.push({ type: "mention", text: message.slice(range.start, range.end), mention: range.mention });
    cursor = range.end;
  });

  if (cursor < message.length) {
    segments.push({ type: "text", text: message.slice(cursor) });
  }

  return segments;
}

/**
 * Flat list of the member ids a post mentions, stored as `mention_ids` beside `mentions` so
 * paeapi's rename sweep can find every post that mentions someone with one `array-contains`
 * query instead of scanning the whole collection.
 */
export function mentionIdsOf(mentions: MentionRecord[] | null | undefined): string[] {
  if (!Array.isArray(mentions)) return [];
  const ids = mentions.map((m) => (m && m.id !== undefined && m.id !== null ? `${m.id}` : "")).filter((id) => id !== "");
  return Array.from(new Set(ids));
}
